import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';

// Components
import NavBar from './components/NavBar';
import NotificationsManager from './components/NotificationsManager';
import DashboardLayout from './components/DashboardLayout';
import FeederLayout from './components/FeederLayout';
import FeederDashboard from './pages/FeederDashboard';
import FeederCatsPage from './pages/FeederCatsPage';
import CreateFeedingPage from './pages/CreateFeedingPage';
import FeederFeedingsPage from './pages/FeederFeedingsPage';
import AddCatPage from './pages/AddCatPage';
import MobileInstallPrompt from './components/MobileInstallPrompt';
import NotificationTester from './components/NotificationTester';

// Pages
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import FeederLoginPage from './pages/FeederLoginPage';
import UserDashboard from './pages/UserDashboard';
import AllCatsPage from './pages/AllCatsPage';
import RequestedFeedingsPage from './pages/RequestedFeedingsPage';
import FeedingHistoryPage from './pages/FeedingHistoryPage';
import UserProfile from './pages/UserProfile';
import UserSettings from './pages/UserSettings';
import FeederProfile from './pages/FeederProfile';
import CatDetailsPage from './pages/CatDetailsPage';
import EditCatPage from './pages/EditCatPage';

// Context
import { AuthProvider } from './context/AuthContext';

// Utils
import { diagnosePwaInstallation } from './utils/pwa';

// Auth Guard for protected routes
const ProtectedRoute = ({ children }) => {
  const isAuthenticated = localStorage.getItem('token') !== null;
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Protected route for feeders
const FeederRoute = ({ children }) => {
  const isAuthenticated = localStorage.getItem('token') !== null;
  const userType = localStorage.getItem('userType');
  const isFeeder = userType === 'feeder';
  
  if (!isAuthenticated) {
    return <Navigate to="/feeder-login" />;
  }
  
  if (!isFeeder) {
    return <Navigate to="/unauthorized" />;
  }
  
  return children;
};

function App() {
  const [installPrompt, setInstallPrompt] = useState(null);
  
  // Listen for the beforeinstallprompt event to handle PWA installation
  useEffect(() => {
    const handleBeforeInstallPrompt = (event) => {
      // Prevent the default browser prompt
      event.preventDefault();
      // Save the event for later use
      setInstallPrompt(event);
      console.log('beforeinstallprompt event fired and saved', event);
    };
    
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    
    // Run PWA installation diagnostics
    setTimeout(() => {
      const diagnostics = diagnosePwaInstallation();
      console.log('PWA Installation Diagnostics:', diagnostics);
    }, 2000);
    
    // Listen for app installed event
    window.addEventListener('appinstalled', (event) => {
      console.log('App was installed', event);
      // Clear the prompt
      setInstallPrompt(null);
    });
    
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', (event) => {
        console.log('App was installed', event);
      });
    };
  }, []);
  
  // Handle PWA installation
  const handleInstallClick = () => {
    if (!installPrompt) {
      console.log('No install prompt available');
      return;
    }
    
    // Show the installation prompt
    installPrompt.prompt();
    console.log('Install prompt shown to user');
    
    // Wait for the user to respond to the prompt
    installPrompt.userChoice.then((choiceResult) => {
      if (choiceResult.outcome === 'accepted') {
        console.log('User accepted the install prompt');
      } else {
        console.log('User dismissed the install prompt');
      }
      
      // Reset the install prompt state
      setInstallPrompt(null);
    });
  };
  
  return (
    <AuthProvider>
      <Router>
        <div className="app">
          <Routes>
            {/* Public routes with NavBar */}
            <Route path="/" element={
              <>
                <NavBar installPrompt={installPrompt} onInstallClick={handleInstallClick} />
                <NotificationsManager />
                <main className="main-content">
                  <HomePage installPrompt={installPrompt} onInstallClick={handleInstallClick} />
                </main>
                <footer className="footer">
                  <div className="container">
                    <p>&copy; {new Date().getFullYear()} WellFed Cats. All rights reserved.</p>
                  </div>
                </footer>
              </>
            } />
            
            <Route path="/login" element={
              <>
                <NavBar installPrompt={installPrompt} onInstallClick={handleInstallClick} />
                <main className="main-content">
                  <LoginPage />
                </main>
                <footer className="footer">
                  <div className="container">
                    <p>&copy; {new Date().getFullYear()} WellFed Cats. All rights reserved.</p>
                  </div>
                </footer>
              </>
            } />
            
            <Route path="/register" element={
              <>
                <NavBar installPrompt={installPrompt} onInstallClick={handleInstallClick} />
                <main className="main-content">
                  <RegisterPage />
                </main>
                <footer className="footer">
                  <div className="container">
                    <p>&copy; {new Date().getFullYear()} WellFed Cats. All rights reserved.</p>
                  </div>
                </footer>
              </>
            } />
            
            <Route path="/feeder-login" element={
              <>
                <NavBar installPrompt={installPrompt} onInstallClick={handleInstallClick} />
                <main className="main-content">
                  <FeederLoginPage />
                </main>
                <footer className="footer">
                  <div className="container">
                    <p>&copy; {new Date().getFullYear()} WellFed Cats. All rights reserved.</p>
                  </div>
                </footer>
              </>
            } />
            
            {/* Development-only route for testing push notifications */}
            {import.meta.env.DEV && (
              <Route path="/test-notifications" element={<NotificationTester />} />
            )}
            
            {/* User dashboard routes */}
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }>
              <Route index element={<UserDashboard />} />
              <Route path="cats" element={<AllCatsPage />} />
              <Route path="cats/:catId" element={<CatDetailsPage />} />
              <Route path="feeding-request/open" element={<RequestedFeedingsPage />} />
              <Route path="feeding-request/processed" element={<FeedingHistoryPage />} />
              <Route path="requested-feedings" element={<Navigate to="/dashboard/feeding-request/open" replace />} />
              <Route path="feeding-history" element={<Navigate to="/dashboard/feeding-request/processed" replace />} />
              <Route path="profile" element={<UserProfile />} />
              <Route path="settings" element={<UserSettings />} />
            </Route>
            
            {/* Feeder routes */}
            <Route path="/feeder" element={
              <FeederRoute>
                <FeederLayout />
              </FeederRoute>
            }>
              <Route index element={<FeederDashboard />} />
              <Route path="my-cats" element={<FeederCatsPage />} />
              <Route path="add-cat" element={<AddCatPage />} />
              <Route path="cat/:catId" element={<CatDetailsPage />} />
              <Route path="edit-cat/:catId" element={<EditCatPage />} />
              <Route path="create-feeding/:catId" element={<CreateFeedingPage />} />
              <Route path="feedings" element={<FeederFeedingsPage />} />
              <Route path="profile" element={<FeederProfile />} />
            </Route>
            
            {/* Catch-all route */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          
          {/* Mobile install prompt - shown on all pages */}
          <MobileInstallPrompt 
            installPrompt={installPrompt} 
            onInstallClick={handleInstallClick} 
          />
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App; 